<script setup lang="ts">
/**
 * The touch debrief's fuel button: sits beside *Return to garage* in the pinned
 * footer, the fuel can filling it, and opens a small popup with the same two
 * refills the desktop fuel card sells (`BattleSummary.vue`) — through the shop's
 * own `refillFuel` and tiers, two taps to spend.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import CurrencyIcon from './CurrencyIcon.vue';
import { useGarage } from '@/state/garage';

const garage = useGarage();
const wallet = garage.wallet;
const tiers = computed(() => garage.shop.value?.fuelRefill.tiers ?? []);
const topUpTier = computed(() => tiers.value.find((t) => t.units === 1) ?? null);
const fullTankTier = computed(() => tiers.value.find((t) => t.units === 'full') ?? null);

/** Shown while the tank is not full and a refill is on sale. */
const shown = computed(
  () =>
    !!wallet.value &&
    wallet.value.fuel < wallet.value.fuelCapacity &&
    !!(topUpTier.value?.available || fullTankTier.value?.available),
);

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const armed = ref<1 | 'full' | null>(null);
let disarm: ReturnType<typeof setTimeout> | null = null;

/** Two taps: the first arms the button and shows the spend, the second buys. */
async function buy(units: 1 | 'full'): Promise<void> {
  if (armed.value !== units) {
    armed.value = units;
    if (disarm) clearTimeout(disarm);
    disarm = setTimeout(() => (armed.value = null), 4000);
    return;
  }
  armed.value = null;
  await garage.refillFuel(units);
}

function onOutside(event: PointerEvent): void {
  if (open.value && !root.value?.contains(event.target as Node)) open.value = false;
}
function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape') open.value = false;
}
onMounted(() => {
  document.addEventListener('pointerdown', onOutside);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onOutside);
  document.removeEventListener('keydown', onKey);
  if (disarm) clearTimeout(disarm);
});
</script>

<template>
  <div v-if="shown" ref="root" class="result-fuel">
    <button
      type="button"
      class="can"
      data-test="result-fuel"
      :aria-expanded="open"
      aria-label="Buy fuel"
      @click="open = !open"
    >
      <img src="/assets/store/fuel-refill.webp" alt="" decoding="async" />
      <span class="can-label">+Fuel</span>
    </button>

    <div v-if="open" class="pop" role="dialog" aria-label="Buy fuel" data-test="result-fuel-pop">
      <img class="pop-art" src="/assets/store/fuel-refill.webp" alt="" decoding="async" />
      <div class="pop-body">
        <p class="pop-head">
          <span class="pop-label">Fuel</span>
          <span class="pop-level">{{ wallet!.fuel }} / {{ wallet!.fuelCapacity }}</span>
        </p>
        <button
          v-if="fullTankTier?.available"
          type="button"
          class="fuel-btn primary"
          :class="{ armed: armed === 'full' }"
          data-test="result-fuel-full"
          :disabled="garage.busyId.value !== null || wallet!.diamonds < fullTankTier.diamondCost"
          @click="buy('full')"
        >
          <span class="fuel-btn-name">{{ armed === 'full' ? 'Tap to confirm' : 'Full tank' }}</span>
          <span class="fuel-btn-price">{{ fullTankTier.diamondCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></span>
        </button>
        <button
          v-if="topUpTier?.available"
          type="button"
          class="fuel-btn"
          :class="{ armed: armed === 1 }"
          data-test="result-fuel-plus-one"
          :disabled="garage.busyId.value !== null || wallet!.diamonds < topUpTier.diamondCost"
          @click="buy(1)"
        >
          <span class="fuel-btn-name">{{ armed === 1 ? 'Tap to confirm' : '+1 fuel' }}</span>
          <span class="fuel-btn-price">{{ topUpTier.diamondCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.result-fuel {
  position: relative;
}

/* The can fills the button; "+Fuel" sits on a shade across its foot. */
.can {
  position: relative;
  display: block;
  width: 44px;
  height: 44px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(232, 114, 44, 0.75);
  border-radius: 3px;
  background: #1a1f26;
  cursor: pointer;
}

.can img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.can-label {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.12rem 0 0.1rem;
  background: rgba(6, 8, 11, 0.72);
  color: #ffc98a;
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
}

.can[aria-expanded='true'] {
  border-color: #fff;
}

/* The popup opens upward from the footer. */
.pop {
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.5rem);
  display: flex;
  gap: 0.6rem;
  width: 15.5rem;
  padding: 0.55rem;
  border: 1px solid var(--steel-600, #2a3340);
  border-top: 2px solid var(--accent, #e8722c);
  border-radius: 6px;
  background: var(--steel-800, #12161c);
  box-shadow: 0 16px 30px -10px rgba(0, 0, 0, 0.8);
}

.pop-art {
  width: 4.2rem;
  height: 4.2rem;
  flex: none;
  object-fit: cover;
  border-radius: 4px;
}

.pop-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.pop-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 0;
}

.pop-label {
  color: var(--steel-400, #63748a);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.pop-level {
  color: #fff;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* The refill buttons are the desktop fuel card's (`BattleSummary.vue`), copied. */
.fuel-btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.4rem;
  min-height: 44px;
  padding: 0.2rem 0.6rem;
  border: 1px solid var(--steel-400, #78838f);
  border-radius: 4px;
  background: linear-gradient(180deg, #3b4552, #232a33);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 3px 0 #11151a;
  color: #fff;
  font: inherit;
  cursor: pointer;
}

.fuel-btn.primary {
  border-color: #ffc489;
  /* A fill, not a gradient: style.css lays the steel texture over `background-color`. */
  background: #f07a30;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    0 3px 0 #7a3410;
  color: #1a0c04;
}

.fuel-btn:active:not(:disabled) {
  transform: translateY(2px);
}

.fuel-btn.armed {
  outline: 2px solid rgba(255, 255, 255, 0.9);
  outline-offset: 2px;
}

.fuel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fuel-btn-name {
  font-size: 0.74rem;
  font-weight: 700;
}

.fuel-btn-price {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>
